import { useState, useEffect } from "react";
import axios from "axios";
import { Zap, Mail, MessageSquare, Clock, Save, Loader2, Bell, Check, Copy, Eye, RotateCcw, Palette, Plus, Trash2, Star, BookOpen } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPE_LABELS = {
  qualified_email: "E-Mail: Einladung",
  reminder_email: "E-Mail: Erinnerung (48h)",
  qualified_sms: "SMS: Einladung",
  reminder_sms: "SMS: Erinnerung (48h)"
};

const TYPE_ICONS = {
  qualified_email: Mail,
  reminder_email: Clock,
  qualified_sms: MessageSquare,
  reminder_sms: Clock
};

export default function AdminAutomation({ authToken, projectId }) {
  const [library, setLibrary] = useState([]);
  const [settings, setSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewTitle, setPreviewTitle] = useState("");
  const [editTemplate, setEditTemplate] = useState(null);
  const [activeTab, setActiveTab] = useState("qualified_email");
  const [showGuide, setShowGuide] = useState(false);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${authToken}` }
  });

  useEffect(() => {
    fetchLibrary();
    fetchSettings();
  }, [projectId]);

  const fetchLibrary = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/template-library/${projectId}`, getAuthHeaders());
      let templates = response.data.templates || [];
      // If empty, seed defaults
      if (templates.length === 0) {
        await axios.post(`${API}/admin/template-library/${projectId}/seed-defaults`, {}, getAuthHeaders());
        const resp2 = await axios.get(`${API}/admin/template-library/${projectId}`, getAuthHeaders());
        templates = resp2.data.templates || [];
      }
      setLibrary(templates);
    } catch (error) {
      console.error("Error fetching library:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/automation-templates/${projectId}`, getAuthHeaders());
      setSettings(response.data);
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  const handleActivate = async (templateId) => {
    try {
      await axios.post(`${API}/admin/template-library/${projectId}/${templateId}/activate`, {}, getAuthHeaders());
      toast.success("Vorlage aktiviert!");
      fetchLibrary();
      fetchSettings();
    } catch (error) {
      toast.error("Fehler beim Aktivieren");
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm("Vorlage wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/admin/template-library/${projectId}/${templateId}`, getAuthHeaders());
      toast.success("Vorlage gelöscht");
      fetchLibrary();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const handleSaveEdit = async () => {
    if (!editTemplate) return;
    setIsSaving(true);
    try {
      const payload = {
        name: editTemplate.name,
        type: editTemplate.type,
        subject: editTemplate.subject,
        html_content: editTemplate.html_content,
        sms_content: editTemplate.sms_content,
        is_active: editTemplate.is_active || false
      };
      if (editTemplate.id && !editTemplate._isNew) {
        await axios.put(`${API}/admin/template-library/${projectId}/${editTemplate.id}`, payload, getAuthHeaders());
        toast.success("Vorlage gespeichert!");
      } else {
        await axios.post(`${API}/admin/template-library/${projectId}`, payload, getAuthHeaders());
        toast.success("Neue Vorlage erstellt!");
      }
      setEditTemplate(null);
      fetchLibrary();
      fetchSettings();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    } finally {
      setIsSaving(false);
    }
  };

  const handlePreview = async (htmlContent, title) => {
    try {
      const response = await axios.post(
        `${API}/admin/automation-templates/${projectId}/preview`,
        { html: htmlContent },
        getAuthHeaders()
      );
      setPreviewHtml(response.data.html);
      setPreviewTitle(title);
    } catch (error) {
      toast.error("Vorschau-Fehler");
    }
  };

  const handleSavePhone = async () => {
    try {
      await axios.put(`${API}/admin/automation-templates/${projectId}`, {
        contact_phone: settings.contact_phone,
        termin_mode: settings.termin_mode,
        termin_manual_date: settings.termin_manual_date,
        termin_manual_time: settings.termin_manual_time,
        termin_auto_days: settings.termin_auto_days,
        termin_auto_time: settings.termin_auto_time,
        reminder_hours: settings.reminder_hours
      }, getAuthHeaders());
      toast.success("Einstellungen gespeichert!");
    } catch (error) {
      toast.error("Fehler");
    }
  };

  const filteredTemplates = library.filter(t => t.type === activeTab);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-automation">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Zap className="w-6 h-6 text-amber-500" />
            Automatisierung & Vorlagen
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Vorlagen-Bibliothek für automatische E-Mails & SMS
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setShowGuide(true)}
            variant="outline"
            className="rounded-lg text-blue-600 border-blue-200 hover:bg-blue-50"
            data-testid="show-guide-btn"
          >
            <BookOpen className="w-4 h-4 mr-2" />
            Anwendungsablauf
          </Button>
          <Button
            onClick={() => setEditTemplate({ _isNew: true, name: "", type: activeTab, subject: "", html_content: "", sms_content: "", is_active: false })}
            className="bg-emerald-600 hover:bg-emerald-700 rounded-lg"
            data-testid="new-template-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Neue Vorlage
          </Button>
        </div>
      </div>

      {/* Settings Card */}
      <Card className="rounded-xl">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Einstellungen</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Row 1: Phone + Reminder Hours */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-slate-500 uppercase">Kontakt-Telefonnummer (%kontakt_telefon%)</Label>
              <Input
                value={settings?.contact_phone || ""}
                onChange={(e) => setSettings(prev => ({ ...prev, contact_phone: e.target.value }))}
                placeholder="030-246303330"
                className="rounded-lg mt-1"
                data-testid="contact-phone-input"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500 uppercase">Erinnerung senden nach (Stunden)</Label>
              <Input
                type="number"
                value={settings?.reminder_hours || 48}
                onChange={(e) => setSettings(prev => ({ ...prev, reminder_hours: parseInt(e.target.value) || 48 }))}
                placeholder="48"
                className="rounded-lg mt-1 max-w-[120px]"
                data-testid="reminder-hours-input"
              />
              <p className="text-xs text-slate-400 mt-1">Standard: 48 Stunden nach Qualifizierung</p>
            </div>
          </div>

          {/* Row 2: Termin Settings */}
          <div className="border-t border-slate-100 pt-4">
            <Label className="text-xs text-slate-500 uppercase mb-2 block">Termin-Vorschlag (%termin%) in E-Mails & SMS</Label>
            <div className="flex gap-4 mb-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="termin_mode"
                  checked={(settings?.termin_mode || "auto") === "auto"}
                  onChange={() => setSettings(prev => ({ ...prev, termin_mode: "auto" }))}
                  className="accent-emerald-600"
                />
                <span className="text-sm">Automatisch (nächster Werktag)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="termin_mode"
                  checked={settings?.termin_mode === "manual"}
                  onChange={() => setSettings(prev => ({ ...prev, termin_mode: "manual" }))}
                  className="accent-emerald-600"
                />
                <span className="text-sm">Manuell (festes Datum)</span>
              </label>
            </div>

            {(settings?.termin_mode || "auto") === "auto" ? (
              <div className="flex items-center gap-3 bg-slate-50 rounded-lg p-3">
                <div>
                  <Label className="text-xs text-slate-500">Tage voraus</Label>
                  <Input
                    type="number"
                    value={settings?.termin_auto_days || 1}
                    onChange={(e) => setSettings(prev => ({ ...prev, termin_auto_days: parseInt(e.target.value) || 1 }))}
                    className="rounded-lg w-[80px] mt-1"
                    min={1}
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Uhrzeit</Label>
                  <Input
                    type="time"
                    value={settings?.termin_auto_time || "15:00"}
                    onChange={(e) => setSettings(prev => ({ ...prev, termin_auto_time: e.target.value }))}
                    className="rounded-lg w-[120px] mt-1"
                  />
                </div>
                <p className="text-xs text-slate-400 self-end pb-2">Wochenenden werden übersprungen</p>
              </div>
            ) : (
              <div className="flex items-center gap-3 bg-slate-50 rounded-lg p-3">
                <div>
                  <Label className="text-xs text-slate-500">Datum</Label>
                  <Input
                    type="date"
                    value={settings?.termin_manual_date || ""}
                    onChange={(e) => setSettings(prev => ({ ...prev, termin_manual_date: e.target.value }))}
                    className="rounded-lg w-[180px] mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Uhrzeit</Label>
                  <Input
                    type="time"
                    value={settings?.termin_manual_time || "15:00"}
                    onChange={(e) => setSettings(prev => ({ ...prev, termin_manual_time: e.target.value }))}
                    className="rounded-lg w-[120px] mt-1"
                  />
                </div>
              </div>
            )}
          </div>

          <Button onClick={handleSavePhone} size="sm" className="bg-emerald-600 hover:bg-emerald-700 rounded-lg">
            <Save className="w-4 h-4 mr-1" /> Einstellungen speichern
          </Button>
        </CardContent>
      </Card>

      {/* Type Tabs */}
      <div className="flex gap-2 border-b border-slate-200 pb-2">
        {Object.entries(TYPE_LABELS).map(([key, label]) => {
          const Icon = TYPE_ICONS[key];
          const count = library.filter(t => t.type === key).length;
          const activeCount = library.filter(t => t.type === key && t.is_active).length;
          return (
            <Button
              key={key}
              variant={activeTab === key ? "default" : "ghost"}
              size="sm"
              onClick={() => setActiveTab(key)}
              className={`rounded-lg ${activeTab === key ? "bg-slate-800" : ""}`}
            >
              <Icon className="w-3.5 h-3.5 mr-1.5" />
              {label}
              <Badge className={`ml-2 text-xs ${activeTab === key ? "bg-white/20 text-white" : "bg-slate-200"}`}>
                {count}
              </Badge>
              {activeCount > 0 && (
                <Star className="w-3 h-3 ml-1 fill-amber-400 text-amber-400" />
              )}
            </Button>
          );
        })}
      </div>

      {/* Template Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredTemplates.map((template) => (
          <Card key={template.id} className={`rounded-xl transition-all ${template.is_active ? 'border-2 border-emerald-400 shadow-md' : 'border border-slate-200'}`}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {template.is_active && (
                    <Badge className="bg-emerald-100 text-emerald-700 text-xs flex items-center gap-1">
                      <Star className="w-3 h-3 fill-emerald-500" /> Aktiv
                    </Badge>
                  )}
                  <CardTitle className="text-sm font-semibold">{template.name}</CardTitle>
                </div>
                <div className="flex gap-1">
                  {template.html_content && (
                    <Button size="sm" variant="ghost" onClick={() => handlePreview(template.html_content, template.name)} className="h-7 w-7 p-0 rounded-lg" title="Vorschau">
                      <Eye className="w-3.5 h-3.5 text-blue-500" />
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => setEditTemplate({ ...template })} className="h-7 w-7 p-0 rounded-lg" title="Bearbeiten">
                    <Palette className="w-3.5 h-3.5 text-slate-500" />
                  </Button>
                  {!template.is_active && (
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(template.id)} className="h-7 w-7 p-0 rounded-lg" title="Löschen">
                      <Trash2 className="w-3.5 h-3.5 text-red-400" />
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {template.subject && (
                <p className="text-xs text-slate-500 mb-2 truncate">Betreff: {template.subject}</p>
              )}
              {template.html_content && (
                <div className="border border-slate-100 rounded-lg overflow-hidden h-[120px] bg-slate-50">
                  <iframe
                    srcDoc={template.html_content.replace(/%vorname%/g, 'Max').replace(/%termin%/g, 'Morgen, 15:00').replace(/%kontakt_telefon%/g, settings?.contact_phone || '030-246303330')}
                    title={template.name}
                    className="w-full border-0 pointer-events-none"
                    style={{ height: "240px", transform: "scale(0.5)", transformOrigin: "top left", width: "200%" }}
                    sandbox=""
                  />
                </div>
              )}
              {template.sms_content && (
                <div className="bg-slate-50 border border-slate-100 rounded-lg p-2 text-xs text-slate-600 whitespace-pre-wrap max-h-[200px] overflow-y-auto">
                  {template.sms_content.replace(/%vorname%/g, 'Max').replace(/%termin%/g, 'Morgen, 15:00').replace(/%kontakt_telefon%/g, settings?.contact_phone || '030-246303330')}
                </div>
              )}
              {!template.is_active && (
                <Button
                  size="sm"
                  onClick={() => handleActivate(template.id)}
                  className="mt-3 w-full rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white"
                  data-testid={`activate-${template.id}`}
                >
                  <Star className="w-3.5 h-3.5 mr-1" />
                  Als aktive Vorlage setzen
                </Button>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredTemplates.length === 0 && (
        <div className="text-center py-12 text-slate-400">
          <Mail className="w-10 h-10 mx-auto mb-3" />
          <p>Keine Vorlagen für diesen Typ vorhanden</p>
        </div>
      )}

      {/* Edit/Create Template Dialog */}
      <AlertDialog open={!!editTemplate} onOpenChange={(open) => !open && setEditTemplate(null)}>
        <AlertDialogContent className="max-w-2xl max-h-[90vh] rounded-xl overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {editTemplate?._isNew ? "Neue Vorlage erstellen" : `Vorlage bearbeiten: ${editTemplate?.name}`}
            </AlertDialogTitle>
          </AlertDialogHeader>
          {editTemplate && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500 uppercase">Name</Label>
                  <Input
                    value={editTemplate.name}
                    onChange={(e) => setEditTemplate({ ...editTemplate, name: e.target.value })}
                    placeholder="Meine Vorlage"
                    className="rounded-lg"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500 uppercase">Typ</Label>
                  <Select value={editTemplate.type} onValueChange={(v) => setEditTemplate({ ...editTemplate, type: v })}>
                    <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(TYPE_LABELS).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {(editTemplate.type.includes("email")) && (
                <>
                  <div>
                    <Label className="text-xs text-slate-500 uppercase">Betreff</Label>
                    <Input
                      value={editTemplate.subject || ""}
                      onChange={(e) => setEditTemplate({ ...editTemplate, subject: e.target.value })}
                      placeholder="%vorname%, bitte kurz bestätigen"
                      className="rounded-lg"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label className="text-xs text-slate-500 uppercase">HTML Inhalt</Label>
                      <Button size="sm" variant="ghost" onClick={() => handlePreview(editTemplate.html_content, editTemplate.name)} className="text-xs text-blue-500">
                        <Eye className="w-3 h-3 mr-1" /> Vorschau
                      </Button>
                    </div>
                    <Textarea
                      value={editTemplate.html_content || ""}
                      onChange={(e) => setEditTemplate({ ...editTemplate, html_content: e.target.value })}
                      rows={14}
                      className="rounded-lg font-mono text-xs"
                      placeholder="<!DOCTYPE html>..."
                    />
                    <p className="text-xs text-slate-400 mt-1">Platzhalter: %vorname% %termin% %kontakt_telefon% %telefon% %email%</p>
                  </div>
                </>
              )}

              {(editTemplate.type.includes("sms")) && (
                <div>
                  <Label className="text-xs text-slate-500 uppercase">SMS Text</Label>
                  <Textarea
                    value={editTemplate.sms_content || ""}
                    onChange={(e) => setEditTemplate({ ...editTemplate, sms_content: e.target.value })}
                    rows={8}
                    className="rounded-lg"
                    placeholder="Hi %vorname% ..."
                  />
                  <p className="text-xs text-slate-400 mt-1">Platzhalter: %vorname% %termin% %kontakt_telefon%</p>
                </div>
              )}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg">Abbrechen</AlertDialogCancel>
            <Button onClick={handleSaveEdit} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700 rounded-lg">
              {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Speichern
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Email Preview Dialog */}
      <AlertDialog open={!!previewHtml} onOpenChange={(open) => !open && setPreviewHtml(null)}>
        <AlertDialogContent className="max-w-2xl max-h-[85vh] rounded-xl overflow-hidden">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-blue-500" />
              Vorschau: {previewTitle}
            </AlertDialogTitle>
            <AlertDialogDescription>
              So sieht die E-Mail beim Bewerber aus (Beispiel: "Max Mustermann")
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="border border-slate-200 rounded-lg overflow-auto max-h-[55vh] bg-white">
            <iframe
              srcDoc={previewHtml || ""}
              title="E-Mail Vorschau"
              className="w-full border-0"
              style={{ minHeight: "400px", height: "100%" }}
              sandbox=""
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg">Schließen</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Anwendungsablauf Guide */}
      <AlertDialog open={showGuide} onOpenChange={setShowGuide}>
        <AlertDialogContent className="max-w-2xl max-h-[90vh] rounded-xl overflow-hidden">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-lg">
              <BookOpen className="w-5 h-5 text-blue-500" />
              Anwendungsablauf
            </AlertDialogTitle>
          </AlertDialogHeader>
          <div className="overflow-y-auto max-h-[65vh] pr-2 space-y-6 text-sm">
            {/* Einrichtung */}
            <div>
              <h3 className="font-bold text-base text-slate-800 flex items-center gap-2 mb-2">
                <span className="bg-emerald-100 text-emerald-700 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">1</span>
                Einrichtung (einmalig)
              </h3>
              <div className="bg-slate-50 rounded-lg p-3 space-y-2 text-slate-600">
                <p><strong>Admin &rarr; Automatisierung</strong></p>
                <p><strong>Telefonnummer:</strong> Oben steht die Rückrufnummer (030-246303330). Diese erscheint in allen E-Mails & SMS. Bei Bedarf ändern &rarr; "Speichern".</p>
                <p><strong>Aktive Vorlage:</strong> Die Vorlage mit grünem Rand und &#11088; "Aktiv" wird automatisch versendet. Zum Wechseln auf ein anderes Design &rarr; "Als aktive Vorlage setzen" klicken.</p>
              </div>
            </div>

            {/* Täglicher Ablauf */}
            <div>
              <h3 className="font-bold text-base text-slate-800 flex items-center gap-2 mb-2">
                <span className="bg-blue-100 text-blue-700 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">2</span>
                Täglicher Ablauf
              </h3>
              <div className="space-y-3">
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="font-semibold text-blue-800 mb-1">A) Bewerber anrufen & qualifizieren</p>
                  <p className="text-slate-600">Leads &rarr; Lead anklicken &rarr; Telefonnummer klicken (CallRelay) &rarr; Telefonat führen &rarr; Status auf <strong>"Qualifiziert"</strong> setzen</p>
                </div>
                <div className="bg-emerald-50 rounded-lg p-3">
                  <p className="font-semibold text-emerald-800 mb-1">B) Was passiert automatisch</p>
                  <p className="text-slate-600">&#9993;&#65039; <strong>E-Mail 1 (Einladung)</strong> geht SOFORT automatisch raus (+ BCC an chat@viralehooks.de)</p>
                  <p className="text-slate-600">&#128241; <strong>SMS-Popup</strong> erscheint mit fertigem Text &rarr; "Via CallRelay senden" klicken</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-3">
                  <p className="font-semibold text-amber-800 mb-1">C) 48h-Erinnerung (automatisch)</p>
                  <p className="text-slate-600">&#9993;&#65039; <strong>E-Mail 2 (Erinnerung)</strong> geht nach 48h AUTOMATISCH raus (kein Klick nötig)</p>
                  <p className="text-slate-600">&#128241; Lead erscheint im orangen Tab <strong>"Erinnerung fällig"</strong> in der Leads-Liste</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-3">
                  <p className="font-semibold text-purple-800 mb-1">D) Erinnerungs-SMS senden</p>
                  <p className="text-slate-600">Leads &rarr; Tab <strong>"Erinnerung fällig"</strong> &rarr; "SMS senden" klicken &rarr; Popup &rarr; "Via CallRelay" &rarr; Lead verschwindet aus Liste</p>
                </div>
              </div>
            </div>

            {/* Flow Visualisierung */}
            <div>
              <h3 className="font-bold text-base text-slate-800 flex items-center gap-2 mb-2">
                <span className="bg-violet-100 text-violet-700 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">3</span>
                Flow auf einen Blick
              </h3>
              <div className="bg-gradient-to-b from-slate-50 to-slate-100 rounded-lg p-4 font-mono text-xs text-center space-y-1">
                <p className="font-bold text-slate-700">Telefonat &rarr; Status "Qualifiziert"</p>
                <p className="text-slate-400">&darr;</p>
                <p className="text-emerald-600 font-semibold">E-Mail 1 (automatisch) + SMS 1 (Popup &rarr; CallRelay)</p>
                <p className="text-slate-400">&darr; 48 Stunden</p>
                <p className="text-amber-600 font-semibold">E-Mail 2 (automatisch) + SMS 2 (Tab "Erinnerung fällig" &rarr; CallRelay)</p>
              </div>
            </div>

            {/* Vorlagen verwalten */}
            <div>
              <h3 className="font-bold text-base text-slate-800 flex items-center gap-2 mb-2">
                <span className="bg-pink-100 text-pink-700 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">4</span>
                Vorlagen verwalten
              </h3>
              <div className="bg-slate-50 rounded-lg p-3 text-slate-600">
                <table className="w-full text-xs">
                  <tbody>
                    <tr className="border-b border-slate-200"><td className="py-1.5 font-medium">Vorlage wechseln</td><td className="py-1.5">"Als aktive Vorlage setzen" klicken</td></tr>
                    <tr className="border-b border-slate-200"><td className="py-1.5 font-medium">Vorschau</td><td className="py-1.5">&#128065; Icon an der Vorlage</td></tr>
                    <tr className="border-b border-slate-200"><td className="py-1.5 font-medium">Bearbeiten</td><td className="py-1.5">&#127912; Icon &rarr; HTML/Text ändern &rarr; Speichern</td></tr>
                    <tr className="border-b border-slate-200"><td className="py-1.5 font-medium">Eigenes HTML</td><td className="py-1.5">&#127912; Icon &rarr; HTML-Feld komplett ersetzen &rarr; Speichern</td></tr>
                    <tr className="border-b border-slate-200"><td className="py-1.5 font-medium">Neue Vorlage</td><td className="py-1.5">"+ Neue Vorlage" Button oben rechts</td></tr>
                    <tr><td className="py-1.5 font-medium">Löschen</td><td className="py-1.5">&#128465; Icon (nur nicht-aktive Vorlagen)</td></tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Platzhalter */}
            <div>
              <h3 className="font-bold text-base text-slate-800 flex items-center gap-2 mb-2">
                <span className="bg-cyan-100 text-cyan-700 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">5</span>
                Platzhalter
              </h3>
              <div className="bg-slate-50 rounded-lg p-3">
                <table className="w-full text-xs">
                  <tbody>
                    <tr className="border-b border-slate-200"><td className="py-1.5 font-mono text-purple-600">%vorname%</td><td className="py-1.5 text-slate-600">Vorname des Bewerbers</td></tr>
                    <tr className="border-b border-slate-200"><td className="py-1.5 font-mono text-purple-600">%termin%</td><td className="py-1.5 text-slate-600">Nächster Werktag, 15:00 Uhr (automatisch)</td></tr>
                    <tr className="border-b border-slate-200"><td className="py-1.5 font-mono text-purple-600">%kontakt_telefon%</td><td className="py-1.5 text-slate-600">Die oben eingestellte Rückrufnummer</td></tr>
                    <tr className="border-b border-slate-200"><td className="py-1.5 font-mono text-purple-600">%telefon%</td><td className="py-1.5 text-slate-600">Telefonnummer des Bewerbers</td></tr>
                    <tr><td className="py-1.5 font-mono text-purple-600">%email%</td><td className="py-1.5 text-slate-600">E-Mail des Bewerbers</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg">Schließen</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
